/**
 * One dialog, two presentations — the same contract the POS's `Sheet` has, in MUI markup.
 *
 * **Below 768px it is a bottom sheet**: full width, anchored to the bottom edge, rounded only at
 * the top, sliding up. **At 768px and above it is a centred dialog**, capped and never touching a
 * viewport edge. A modal that floats as a 444px card in the middle of a 360px phone is the shape
 * this replaces — it was what all thirty-nine feature dialogs did before this component existed.
 *
 * ## The width rule
 *
 * Two widths, and a dialog does not get to pick a third:
 *
 * - **480px** — every dialog. A form, a confirmation, a picker, a wizard step.
 * - **600px**, via `wide` — **only** when the dialog's content is a table. Four numeric columns
 *   do not fit in 480 and squeezing them wraps every header.
 *
 * `wide` is not for comfort. A form with eight fields is still 480: a wider form does not become
 * easier to fill in, it becomes a longer line to read and a control row that drifts apart. If a
 * dialog feels cramped at 480 and has no table in it, the dialog is doing too much.
 *
 * ## The action row
 *
 * Cancel on the left as an outline, confirm on the right. On the sheet the two stack full width
 * with confirm on top, because on a phone the thumb is at the bottom and the last button before
 * it should not be the one that discards the work.
 *
 * Danger colour on the confirm **only when the action is genuinely destructive** — deletes data,
 * removes units, revokes access. That is the one place in this product where a semantic colour is
 * correct on an action. "Reset password", "repair" and "create anyway" are not destructive and
 * take the primary.
 *
 * Focus trapping, escape, scroll lock and focus restoration are MUI's, unchanged.
 */

import { forwardRef, type ComponentProps, type ReactElement, type ReactNode, type Ref } from 'react';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import type { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { dialogWidth, radius } from '@pos/tokens';

/** A sheet arrives from the edge it is attached to. MUI needs the ref forwarded to animate it. */
const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  // MUI types the transition slot's props with every field optional and `Slide`'s own with them
  // required, which `exactOptionalPropertyTypes` will not reconcile. The values are MUI's own,
  // passed straight through.
  return <Slide direction="up" ref={ref} {...(props as ComponentProps<typeof Slide>)} />;
});

export type AppDialogProps = {
  open: boolean;
  /** Called on escape and on a backdrop click. Omit to make the dialog non-dismissable. */
  onClose?: (() => void) | undefined;
  /** 600px instead of 480. **Only** for a dialog whose content is a table — see above. */
  wide?: boolean;
  /** `DialogTitle`, `DialogContent`, `DialogActions`, exactly as before. */
  children: ReactNode;
  'aria-labelledby'?: string | undefined;
};

export function AppDialog({
  open,
  onClose,
  wide = false,
  children,
  'aria-labelledby': labelledBy,
}: AppDialogProps) {
  const theme = useTheme();
  const sheet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      open={open}
      fullWidth
      // MUI's own `maxWidth` keys are a different scale from this product's two widths, so the
      // cap is set on the paper below and the key is switched off.
      maxWidth={false}
      {...(onClose !== undefined && { onClose })}
      {...(labelledBy !== undefined && { 'aria-labelledby': labelledBy })}
      {...(sheet && { TransitionComponent: SlideUp })}
      slotProps={{
        paper: {
          sx: sheet
            ? {
                m: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: '90dvh',
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                borderRadius: `${radius.sheet} ${radius.sheet} 0 0`,
                // The home indicator on an iPhone sits over the last 34px of the sheet.
                pb: 'env(safe-area-inset-bottom)',
              }
            : {
                maxWidth: wide ? dialogWidth.table : dialogWidth.default,
                // Never edge to edge on a small laptop either.
                m: 2,
              },
        },
      }}
      sx={{
        // Stated once here so no dialog lays out its own title, padding or action row.
        '& .MuiDialogTitle-root': { fontWeight: 700, px: 3, pt: 2.5, pb: 1.5 },
        '& .MuiDialogContent-root': { px: 3 },
        '& .MuiDialogActions-root': {
          px: 3,
          pt: 1,
          pb: 2.5,
          gap: 1,
          // `DialogActions` spaces its children with a left margin; a column needs that gone or
          // the stacked buttons sit indented from each other.
          '& > :not(style) ~ :not(style)': { ml: 0 },
          ...(sheet
            ? {
                // Reversed, so confirm — last in source order — ends up on top, nearest the thumb.
                flexDirection: 'column-reverse',
                alignItems: 'stretch',
                '& .MuiButton-root': { width: '100%' },
              }
            : { justifyContent: 'flex-end' }),
        },
      }}
    >
      {children}
    </Dialog>
  );
}
